import { Page, View, Text, Image } from '@react-pdf/renderer';
import {
  LaudoHeader,
  Footer,
  Homogeneizacao,
  DetalhamentoValores,
  AvaliacaoFinal,
  ResponsavelLaudo,
  CalculoAvaliatario,
} from '../../../types';
import { avmPageStyles as s } from './styles';
import { AVMPageHeader, AVMSectionHeader, AVMPageFooter } from './components';
import { Style } from '@react-pdf/types';

// Table cell helpers - fixed width, 7px font, centered
const cellH = (w: number): Style => ({
  width: w || undefined,
  flex: w === 0 ? 1 : undefined,
  fontSize: 7,
  fontWeight: 'bold',
  color: '#000000',
  textAlign: 'center',
});
const cellHFlex: Style = { flex: 1, fontSize: 7, fontWeight: 'bold', color: '#000000', textAlign: 'center' };
const cellD = (w: number): Style => ({
  width: w,
  fontSize: 7,
  color: '#000000',
  textAlign: 'center',
});
const cellDFlex: Style = { flex: 1, fontSize: 7, color: '#000000', textAlign: 'center' };

interface Page3CalculosProps {
  header: LaudoHeader;
  tabelaCalculo: CalculoAvaliatario[];
  legendaFatores: string;
  fatoresAtivos?: number;
  mediaValorUnitarioOriginal: string;
  mediaValorHomogeneizado: string;
  homogeneizacao: Homogeneizacao;
  detalhamentoValores: DetalhamentoValores;
  avaliacaoFinal: AvaliacaoFinal;
  responsavelLaudo: ResponsavelLaudo;
  footer: Footer;
  logoBase64?: string;
  metodologia?: string;
  tipoImovel?: string;
  metodologiaAplicada?: string;
  grauPrecisao?: string;
  grauFundamentacao?: string;
  comentarios?: string;
}

export function Page3Calculos({
  header,
  tabelaCalculo,
  legendaFatores,
  fatoresAtivos = 6,
  mediaValorUnitarioOriginal,
  mediaValorHomogeneizado,
  homogeneizacao,
  detalhamentoValores,
  avaliacaoFinal,
  responsavelLaudo,
  footer,
  logoBase64,
  metodologia,
  tipoImovel,
  metodologiaAplicada,
  grauPrecisao,
  grauFundamentacao,
  comentarios,
}: Page3CalculosProps) {
  const fatoresIndices = Array.from({ length: fatoresAtivos }, (_, i) => i + 1);
  const isComparativoDireto = metodologia?.toLowerCase().includes('comparativo') && metodologia?.toLowerCase().includes('direto');
  const isTerreno = (tipoImovel || '').toLowerCase().includes('terreno');

  return (
    <>
    <Page size="A4" style={s.page}>
      <View style={s.pageContent}>
        {/* Header */}
        <AVMPageHeader header={header} logoBase64={logoBase64} />

        {/* ── AVALIAÇÃO DO IMÓVEL - CÁLCULOS (header + metodologia + tabela tudo junto) ── */}
        <View style={{ flexDirection: 'column' }} wrap={false}>
          {metodologiaAplicada && (
            <>
              <AVMSectionHeader title="AVALIAÇÃO DO IMÓVEL - CÁLCULOS" />

              <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6 }}>
                <View style={{ width: 176, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                  <Text style={s.fieldLabel}>Metodologia</Text>
                  <Text style={s.fieldValue}>{metodologiaAplicada}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                  <Text style={s.fieldLabel}>Grau de Precisão</Text>
                  <Text style={s.fieldValue}>{grauPrecisao || '-'}</Text>
                </View>
                <View style={{ flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                  <Text style={s.fieldLabel}>Grau de Fundação</Text>
                  <Text style={s.fieldValue}>{grauFundamentacao || '-'}</Text>
                </View>
              </View>
            </>
          )}

          {/* Table header */}
          <View style={{ flexDirection: 'row', height: 16, paddingHorizontal: 4, marginTop: 8, backgroundColor: '#F5F6F9', alignItems: 'center' }}>
            <Text style={cellH(20)}>ID</Text>
            <Text style={cellH(44)}>Área (m²)</Text>
            <Text style={cellH(80)}>Valor Unit.(R$/m²)</Text>
            {fatoresIndices.map(idx => (
              <Text key={`h-F${idx}`} style={cellH(28)}>F{idx}</Text>
            ))}
            <Text style={cellH(28)}>FAT</Text>
            <Text style={cellH(28)}>SAN</Text>
            <Text style={cellHFlex}>Valor Hom. (R$/m²)</Text>
          </View>

          {/* Table rows */}
          {tabelaCalculo.map((row, index) => (
            <View key={row.id} style={{ flexDirection: 'row', height: 16, paddingHorizontal: 4, backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F6F9', alignItems: 'center' }}>
              <Text style={cellD(20)}>{row.id}</Text>
              <Text style={cellD(44)}>{row.area}</Text>
              <Text style={cellD(80)}>{row.valorUnit}</Text>
              {fatoresIndices.map(idx => (
                <Text key={`r-${row.id}-F${idx}`} style={cellD(28)}>
                  {row[`F${idx}` as keyof CalculoAvaliatario] as string || '-'}
                </Text>
              ))}
              <Text style={cellD(28)}>{row.FAT}</Text>
              <Text style={cellD(28)}>{row.SAN}</Text>
              <Text style={cellDFlex}>{row.valorHomog}</Text>
            </View>
          ))}

          {/* Totals row */}
          <View style={{ flexDirection: 'row', height: 16, paddingHorizontal: 4, backgroundColor: '#F5F6F9', alignItems: 'center' }}>
            <View style={{ width: 20 }} />
            <View style={{ width: 44 }} />
            <Text style={cellH(80)}>{mediaValorUnitarioOriginal}</Text>
            {fatoresIndices.map(idx => (
              <View key={`t-F${idx}`} style={{ width: 28 }} />
            ))}
            <View style={{ width: 28 }} />
            <View style={{ width: 28 }} />
            <Text style={cellHFlex}>{mediaValorHomogeneizado}</Text>
          </View>

          {/* Legenda */}
          <View style={{ paddingHorizontal: 16, paddingVertical: 4 }}>
            <Text style={{ fontSize: 7, color: '#000000' }}>
              {legendaFatores}
            </Text>
          </View>
        </View>

        {/* ── HOMOGENEIZAÇÃO E SANEAMENTO ── */}
        <AVMSectionHeader title="HOMOGENEIZAÇÃO E SANEAMENTO" />

        <View style={{ flexDirection: 'row', gap: 8, paddingHorizontal: 8, paddingVertical: 8 }}>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.fieldLabel}>Nº Elementos</Text>
            <Text style={s.fieldValue}>{homogeneizacao.nElementos}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.fieldLabel}>Valor Unitário Médio</Text>
            <Text style={s.fieldValue}>{homogeneizacao.valorUnitarioMedio}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.fieldLabel}>Limite inferior -30% (R$/m²)</Text>
            <Text style={s.fieldValue}>{homogeneizacao.limiteInferior30}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
            <Text style={s.fieldLabel}>Limite superior +30% (R$/m²)</Text>
            <Text style={s.fieldValue}>{homogeneizacao.limiteSuperior30}</Text>
          </View>
        </View>

        {/* ── DETALHAMENTO DOS VALORES ── */}
        <AVMSectionHeader title="DETALHAMENTO DOS VALORES" />

        <View style={{ flexDirection: 'column' }}>
          {/* Terreno - apenas Método Evolutivo */}
          {!isComparativoDireto && (
            <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' }}>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Área Terreno (m²)</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.areaTerreno}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Valor Unitário (R$/m²)</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.valorUnitarioTerreno}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Valor Parcial</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.valorParcialTerreno}</Text>
              </View>
              <View style={{ flex: 1 }} />
            </View>
          )}

          {/* Área Privativa / Construída */}
          {!isTerreno && (
            <View style={{ flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' }}>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>{isComparativoDireto ? 'Área Averbada (m²)' : 'Área Construída (m²)'}</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.areaConstruida}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Valor Unitário (R$/m²)</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.valorUnitarioConstruida}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Valor Parcial (R$)</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.valorParcialConstruida}</Text>
              </View>
              <View style={{ flex: 1, flexDirection: 'column', gap: 2 }}>
                <Text style={s.fieldLabel}>Valor de Avaliação</Text>
                <Text style={s.fieldValue}>{detalhamentoValores.valorTotal}</Text>
              </View>
            </View>
          )}
        </View>

        {/* ── VALORES DE GARANTIA E LEILÃO ── */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#001F3F', paddingHorizontal: 40, paddingVertical: 16, marginTop: 4 }}>
          <View style={{ flexDirection: 'column', gap: 2 }}>
            <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: 'normal' }}>Valor para Efeito de Garantia</Text>
            <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold' }}>{avaliacaoFinal.valorAvaliacaoGarantia}</Text>
          </View>
          <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: 'normal', textAlign: 'right' }}>Valor para Efeito de Leilão</Text>
            <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 'bold', textAlign: 'right' }}>{avaliacaoFinal.valorGarantiaLeilao}</Text>
          </View>
        </View>

      </View>

      <AVMPageFooter footer={footer} />
    </Page>

    {/* ══ Página separada: Comentários + Responsável pelo Laudo ══ */}
    <Page size="A4" style={s.page}>
      <View style={s.pageContent}>
        <AVMPageHeader header={header} logoBase64={logoBase64} />

        {/* ── COMENTÁRIOS E OBSERVAÇÕES ── */}
        {comentarios && comentarios.trim() && (
          <>
            <AVMSectionHeader title="COMENTÁRIOS E OBSERVAÇÕES" />
            <View style={{ paddingHorizontal: 8, paddingVertical: 8 }}>
              <Text style={{ ...s.fieldValue, fontWeight: 'normal', lineHeight: 1.6, textAlign: 'justify' }}>
                {comentarios}
              </Text>
            </View>
          </>
        )}

        {/* ── RESPONSÁVEL PELO LAUDO ── */}
        <AVMSectionHeader title="RESPONSÁVEL PELO LAUDO" />

        <View style={s.signatureContainer}>
          <Text style={{ fontSize: 8, color: 'rgba(17,19,24,0.70)', marginBottom: 8 }}>
            {responsavelLaudo.localData}
          </Text>

          {responsavelLaudo.imagemAssinaturaCarimbo && (
            <Image
              src={responsavelLaudo.imagemAssinaturaCarimbo}
              style={{ width: 160, height: 50, objectFit: 'contain', marginBottom: 4 }}
            />
          )}

          {responsavelLaudo.nomeResponsavel && (
            <Text style={s.signatureName}>{responsavelLaudo.nomeResponsavel}</Text>
          )}
          {responsavelLaudo.creaResponsavel && (
            <Text style={s.signatureInfo}>CREA {responsavelLaudo.creaResponsavel}</Text>
          )}
        </View>
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
    </>
  );
}
